import styled from '@emotion/styled';
import type { ActivityLayoutItem } from 'ts-activities/types';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  NotesRichTextEditor,
  parseMentions,
} from '__components/Inputs/NotesRichTextEditor';
import { RequiredAsterisk } from '__components/Inputs/InputControl';
import { NotesLabel } from 'pages/Common/styles/Profile';

import { FORMDATA_FIELDS } from '../config';
import { ActivityFields, StyledSection } from '../styled';
import ActivityService from 'services/ActivityService';

import { isDefaultUndeletableNotesField, isOwnerField } from 'checks/fields';
import FieldInput from '__components/Fields/FieldInput';
import { getDataQAForInput } from 'components/Inputs/helpers';
import { fieldModifier } from '../helpers';
import { isEmailStatusField } from 'checks/fields';
import { getPercentageChanceToCloseTooltip } from 'pages/CustomObjects/RecordsPage/helpers';
import { ACTIVITY_FIELD_VALUE_FILE_UPLOAD_SOURCE } from 'hooks/uploadFiles/useUploadFile';
import { isContact } from 'utility/fieldHelpers';
import { _modalSize } from 'components/Modals';
import { OBJECT_ASSOCATIONS } from 'components/GenericWizard/wizards/scheduleActivity';
import { useCompleteActivityWizardContext } from '../context';
import { PluginContext } from 'ts-components/Plugins/context';
import { getFieldsDescription } from 'components/Fields/FieldInput/helpers';
import { DESCRIPTION_VISIBILITY } from 'utility/constants';
import { TextViewer } from '__components/WYSIWYG';

type CompleteActivityFieldsProps = {
  activityId: string;
  associationMode:
    | 'selected_objects_associated'
    | 'all_objects_associated'
    | 'no_objects_associated';
  showErrors: (boolean | { message: string })[];
  scheduledActivityId?: string;
  getKeyListenersProps(id: string): {
    fieldId: string;
    shouldUseKeyboardEventHandle: boolean;
    onBlur(): void;
    onFocus(id: string): void;
  };
  setShowErrors: Dispatch<SetStateAction<(boolean | { message: string })[]>>;
};

const { NOTES, MENTIONS, FIELDS } = FORMDATA_FIELDS;

const LayoutItemContainer = styled.div`
  margin-bottom: 20px;
`;

const CompleteActivityFields = (props: CompleteActivityFieldsProps) => {
  const {
    associationMode,
    activityId,
    showErrors,
    getKeyListenersProps,
    setShowErrors,
    scheduledActivityId,
  } = props;

  const {
    activity,
    activityFields,
    formData,
    isRuleActiveForLayoutItem,
    updateField,
  } = useCompleteActivityWizardContext();

  const { fields, notes, relatedObjects } = formData;

  const fieldsById = useMemo(() => {
    return activityFields.reduce<Record<string, any>>((acc, field) => {
      acc[field.id] = field;
      return acc;
    }, {});
  }, [activityFields]);

  const { t } = useTranslation();
  const { dataAdornments } = useContext(PluginContext);

  const { shouldDisableField } = useCompleteActivityWizardContext();

  const setField = useCallback(
    (id: string, value: any) =>
      updateField(
        FIELDS,
        (prev: any) =>
          prev.map((prevField: any) =>
            prevField.id === id ? { ...prevField, value } : prevField
          ),
        false
      ),
    [updateField]
  );

  if (!activity || !activityFields.length) {
    return null;
  }

  return (
    <StyledSection
      index={
        associationMode === OBJECT_ASSOCATIONS.NO_OBJECT_ASSOCIATIONS ? 0 : 1
      }
      header={t('Complete Activity')}
      isActive
      isLastIndex
    >
      <ActivityFields>
        {activity.layout.map((item: ActivityLayoutItem, i: number) => {
          if (item.type === 'field') {
            const field = fieldsById[item.fieldId];
            if (
              !field ||
              field.isHidden ||
              !isRuleActiveForLayoutItem(item.fieldId)
            ) {
              return null;
            }
            const customObjectField = field.customObjectField;
            // we have to left only custom activity field associated to entities & activity fields
            const fieldWithvalue = fields.find(
              ({ id, customObject, value }) =>
                id === field.id &&
                (!customObject ||
                  relatedObjects.some(
                    (obj) => obj.customObject === customObject
                  ) ||
                  value)
            );

            const isFieldDisabled =
              shouldDisableField(field) ||
              !!(
                isEmailStatusField(field) &&
                customObjectField !== null &&
                isContact(customObjectField.customObject) &&
                relatedObjects.find(
                  ({ customObject }) =>
                    customObjectField!.customObject.id === customObject
                ) &&
                field.options.find(
                  ({ id }: any) => fieldWithvalue?.value === id
                )?.code === 'suppression_list'
              );

            const percentageChanceToCloseProps = {
              ...(fieldWithvalue?.valuePlaceholder &&
              !fieldWithvalue?.value &&
              fieldWithvalue?.value !== 0
                ? {
                    info: getPercentageChanceToCloseTooltip(t),
                    placeholder: fieldWithvalue?.valuePlaceholder,
                  }
                : {}),
            };
            let fieldTypeProps = {};

            if (field.fieldType === 'longtext') {
              fieldTypeProps = {
                initialHeight: 106, // 5 lines of text
              };
            }
            const { withNameDescription } = getFieldsDescription(field, [
              DESCRIPTION_VISIBILITY[0].value,
              DESCRIPTION_VISIBILITY[1].value,
            ]);
            const additionalProps = {
              labelInfo: withNameDescription,
              description: withNameDescription,
            };

            let fetchUrl = customObjectField?.customObject?.fetchUrl;
            if (fetchUrl && ['pipeline', 'standard'].includes(fetchUrl)) {
              fetchUrl = `custom-objects/${customObjectField!.customObject.id}`;
            }

            return (
              <LayoutItemContainer
                key={item.fieldId}
                className={item.columns === 2 ? 'span2' : 'span1'}
              >
                {isDefaultUndeletableNotesField(field) ? (
                  <NotesRichTextEditor
                    {...getKeyListenersProps(field.id)}
                    inModal
                    label={
                      <NotesLabel>
                        {t('Notes')}
                        {field.isRequired && (
                          <RequiredAsterisk>*</RequiredAsterisk>
                        )}
                      </NotesLabel>
                    }
                    value={notes}
                    onChange={({ editor }: any) => {
                      const text = editor.getHTML();
                      updateField(NOTES, text);
                      updateField(MENTIONS, parseMentions(text));
                    }}
                  />
                ) : (
                  <FieldInput
                    portalDateInput
                    key={isFieldDisabled}
                    source={ACTIVITY_FIELD_VALUE_FILE_UPLOAD_SOURCE}
                    isActivityField
                    field={fieldModifier(field, relatedObjects)}
                    disabled={isFieldDisabled}
                    adornments={dataAdornments}
                    fetchUrl={fetchUrl}
                    value={fieldWithvalue?.value ?? undefined}
                    onChange={(val: any) => {
                      if (showErrors[i]) {
                        setShowErrors(
                          showErrors.map((err, j) => (i === j ? false : err))
                        );
                      }
                      setField(field.id, val);
                    }}
                    isGetFullObject
                    underline={false}
                    error={showErrors?.[i]}
                    serviceToUse={ActivityService}
                    objectId={activityId}
                    activityId={scheduledActivityId}
                    adornmentObjectId={relatedObjects?.[0]?.customObject}
                    adornmentEntityId={relatedObjects?.[0]?.entityId}
                    isShowAsterisk={field.isRequired && !isOwnerField(field)}
                    required={field.isRequired && !isOwnerField(field)}
                    inModal
                    {...getDataQAForInput(
                      `activity-${field.name}`,
                      field.fieldType
                    )}
                    {...additionalProps}
                    {...percentageChanceToCloseProps}
                    {...getKeyListenersProps(field.id)}
                    {...fieldTypeProps}
                  />
                )}
              </LayoutItemContainer>
            );
          }

          if (!isRuleActiveForLayoutItem(item.internalName)) {
            return null;
          }

          return (
            <LayoutItemContainer
              key={item.internalName}
              className={item.columns === 2 ? 'span2' : 'span1'}
            >
              <TextViewer content={item.label} />
            </LayoutItemContainer>
          );
        })}
      </ActivityFields>
    </StyledSection>
  );
};

export default CompleteActivityFields;
